import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Upload, X } from 'lucide-react';
import {
  Sheet,
  SheetContent,
} from '../../ui/sheet';
import {
  FUNDING_RIGHT_DRAWER_CLASS,
} from './fundingDrawerUi';
import { Button } from '../../ui/button';
import {
  APPLICATION_TYPE_OPTIONS,
  DECISION_OPTIONS,
  LENDER_OPTIONS,
  LENDING_PRODUCT_OPTIONS,
  PERS_BIZ_OPTIONS,
  ZERO_PERCENT_TERM_OPTIONS,
} from '../../../constants/fundingApplicationOptions';
import BureauPullSelector, {
  bureauPullScoresFromApplication,
  inferSelectedBureauPull,
} from './BureauPullSelector';
import FundingCustomSelect from './FundingCustomSelect';
import SecureEinCell from './SecureEinCell';
import TimeDurationSelect from './TimeDurationSelect';
import {
  applicantsFromSelections,
  applicantOwnerIdsFromSelections,
  buildDefaultApplicantSelections,
  entitySelectLabel,
  formatOwnerSensitiveValue,
  getClientEntities,
  getDefaultEntityId,
  getEntityById,
  ownerBadgeLabel,
  ownershipTotalFromSelections,
  primaryOwnerFromSelections,
  ROLE_ON_APPLICATION_OPTIONS,
} from './fundingClientEntities';
import { isRealPersistedClient } from '../../../services/funding/fundingClientsService';
import { CONFIDENCE_LABELS } from './mockApplicationScan';
import {
  EXTRACTION_STATUS,
  EXTRACTION_UNAVAILABLE_DETAIL,
  EXTRACTION_UNAVAILABLE_TITLE,
  extractApplicationDocument,
} from '../../../services/funding/documentExtractionService';

const emptyDraft = () => ({
  dateOfApplication: '',
  lender: '',
  lendingProduct: '',
  applicationType: '',
  personalOrBusiness: 'Unknown',
  zeroPercentOffer: 'No',
  approvalAmount: '',
  decision: '',
  zeroPercentTerm: 'None',
  ownerName: '',
  ssnMasked: '',
  dob: '',
  fullEin: '',
  businessLegalName: '',
  businessStartDate: '',
  employerName: '',
  timeLiveInHouseYears: 0,
  timeLiveInHouseMonths: 0,
  timeWorkingAtYears: 0,
  timeWorkingAtMonths: 0,
  personalIncome: '',
  businessIncome: '',
  housing: '',
  businessExpenses: '',
  savings: '',
  retirementAccount: '',
  personalAddress: '',
  businessAddress: '',
  notes: '',
});

const ConfidenceBadge = ({ level }) => {
  if (!level) return null;
  return (
    <span className={`fg-confidence fg-confidence-${level}`} data-testid={`confidence-${level}`}>
      {CONFIDENCE_LABELS[level] || level}
    </span>
  );
};

const FieldLabel = ({ htmlFor, children, confidence }) => (
  <label htmlFor={htmlFor} className="fg-create-field-label">
    <span>
      {children}
      {confidence ? <ConfidenceBadge level={confidence} /> : null}
    </span>
  </label>
);

const CreateApplicationDrawer = ({
  open,
  onOpenChange,
  client,
  application = null,
  clientId,
  agencyId,
  onSave,
  saving = false,
  onOpenIdentity,
}) => {
  const fileInputRef = useRef(null);
  const [mode, setMode] = useState('manual');
  const [drawerEntityId, setDrawerEntityId] = useState('');
  const [applicantSelections, setApplicantSelections] = useState({});
  const [draft, setDraft] = useState(emptyDraft());
  const [selectedBureauPull, setSelectedBureauPull] = useState(null);
  const [bureauPullScores, setBureauPullScores] = useState({ experian: null, transunion: null, equifax: null });
  const [confidence, setConfidence] = useState({});
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadedFileName, setUploadedFileName] = useState('');
  const [scanStatus, setScanStatus] = useState('idle');
  const [scanComplete, setScanComplete] = useState(false);
  const [scanning, setScanning] = useState(false);

  const entities = useMemo(() => getClientEntities(client), [client]);
  const isRealClient = isRealPersistedClient(client);
  const hasEntities = entities.length > 0;
  const drawerEntity = useMemo(
    () => getEntityById(client, drawerEntityId),
    [client, drawerEntityId],
  );
  const hasOwners = (drawerEntity?.owners?.length ?? 0) > 0;

  const ownershipTotal = useMemo(
    () => ownershipTotalFromSelections(applicantSelections),
    [applicantSelections],
  );

  const resetFromApplication = useCallback((app) => {
    if (!app) {
      const defaultEntityId = getDefaultEntityId(client);
      const entity = getEntityById(client, defaultEntityId);
      const selections = buildDefaultApplicantSelections(entity);
      const primary = primaryOwnerFromSelections(entity, selections);
      setMode('manual');
      setDrawerEntityId(defaultEntityId);
      setApplicantSelections(selections);
      setDraft({
        ...emptyDraft(),
        ownerName: primary?.name || '',
        ssnMasked: primary?.ssnMasked || '',
        dob: primary?.dob || '',
        fullEin: entity?.einMasked || '',
        businessLegalName: entity?.businessLegalName || client?.businessName || '',
        businessStartDate: entity?.businessStartDate || '',
        businessAddress: entity?.businessAddress || '',
        personalAddress: primary?.personalAddress || '',
        personalIncome: primary?.income ? String(primary.income) : '',
        employerName: primary?.employer || '',
      });
      setBureauPullScores({ experian: null, transunion: null, equifax: null });
      setSelectedBureauPull(null);
      setConfidence({});
      setUploadedFile(null);
      setUploadedFileName('');
      setScanStatus('idle');
      setScanComplete(false);
      return;
    }

    const entityId = app.entityId || getDefaultEntityId(client);
    const entity = getEntityById(client, entityId);
    const scores = bureauPullScoresFromApplication(app);
    const selections = {};
    if (app.applicants?.length && entity?.owners?.length) {
      entity.owners.forEach((owner) => {
        const match = app.applicants.find((a) => a.ownerId === owner.id);
        selections[owner.id] = {
          selected: Boolean(match),
          roleOnApplication: match?.roleOnApplication || 'Co-applicant',
          ownershipPercentUsed: match?.ownershipPercentUsed ?? owner.ownershipPercent ?? '',
        };
      });
    } else if (entity) {
      Object.assign(selections, buildDefaultApplicantSelections(entity));
    }

    setMode(app.source === 'uploaded_scan' ? 'upload' : 'manual');
    setDrawerEntityId(entityId);
    setApplicantSelections(selections);
    setDraft({
      ...emptyDraft(),
      ...app,
      approvalAmount: app.approvalAmount ?? '',
      personalIncome: app.personalIncome ?? '',
      businessIncome: app.businessIncome ?? '',
      housing: app.housing ?? '',
      businessExpenses: app.businessExpenses ?? '',
      savings: app.savings ?? '',
      retirementAccount: app.retirementAccount ?? '',
      zeroPercentOffer: app.zeroPercentOffer || 'No',
      ssnMasked: app.ssnMasked || '',
      businessLegalName: app.businessLegalName || entity?.businessLegalName || '',
      fullEin: app.einMasked || app.fullEin || entity?.einMasked || '',
    });
    setBureauPullScores(scores);
    setSelectedBureauPull(inferSelectedBureauPull(app));
    setConfidence(app.extractionConfidence || {});
    setUploadedFileName(app.uploadedFileName || '');
    setScanStatus(app.scanStatus || 'idle');
    setScanComplete(app.source === 'uploaded_scan' && app.scanStatus === EXTRACTION_STATUS.COMPLETE);
  }, [client]);

  useEffect(() => {
    if (!open) return;
    resetFromApplication(application);
  }, [open, application, resetFromApplication]);

  const setField = (key, value) => setDraft((prev) => ({ ...prev, [key]: value }));

  const handleEntityChange = (entityId) => {
    setDrawerEntityId(entityId);
    const entity = getEntityById(client, entityId);
    if (!entity) {
      setApplicantSelections({});
      return;
    }
    const selections = buildDefaultApplicantSelections(entity);
    const primary = primaryOwnerFromSelections(entity, selections);
    setApplicantSelections(selections);
    setDraft((prev) => ({
      ...prev,
      ownerName: primary?.name || '',
      ssnMasked: primary?.ssnMasked || '',
      dob: primary?.dob || '',
      fullEin: entity?.einMasked || '',
      businessLegalName: entity?.businessLegalName || '',
      businessStartDate: entity?.businessStartDate || '',
      businessAddress: entity?.businessAddress || '',
      personalAddress: primary?.personalAddress || '',
      personalIncome: primary?.income ? String(primary.income) : prev.personalIncome,
      employerName: primary?.employer || prev.employerName,
    }));
  };

  const syncIdentityFromApplicants = useCallback((selections, entity) => {
    const primary = primaryOwnerFromSelections(entity, selections);
    if (!primary) return;
    setDraft((prev) => ({
      ...prev,
      ownerName: primary.name || prev.ownerName,
      ssnMasked: primary.ssnMasked || prev.ssnMasked,
      dob: primary.dob || prev.dob,
      personalAddress: primary.personalAddress || prev.personalAddress,
      employerName: primary.employer || prev.employerName,
      personalIncome: primary.income ? String(primary.income) : prev.personalIncome,
    }));
  }, []);

  const toggleOwner = (ownerId, checked) => {
    setApplicantSelections((prev) => {
      const next = {
        ...prev,
        [ownerId]: {
          ...prev[ownerId],
          selected: checked,
        },
      };
      syncIdentityFromApplicants(next, drawerEntity);
      return next;
    });
  };

  const updateOwnerField = (ownerId, key, value) => {
    setApplicantSelections((prev) => {
      const next = {
        ...prev,
        [ownerId]: { ...prev[ownerId], [key]: value },
      };
      if (key === 'selected' || key === 'roleOnApplication') {
        syncIdentityFromApplicants(next, drawerEntity);
      }
      return next;
    });
  };

  const handleFileUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploadedFile(file);
    setUploadedFileName(file.name);
    setScanStatus('ready');
    setScanComplete(false);
    setConfidence({});
  };

  const clearUpload = () => {
    setUploadedFile(null);
    setUploadedFileName('');
    setScanStatus('idle');
    setScanComplete(false);
    setConfidence({});
    setScanning(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const scanAndFillFields = async () => {
    if (!uploadedFile && !uploadedFileName) return;
    setScanning(true);
    setScanStatus(EXTRACTION_STATUS.SCANNING);
    setScanComplete(false);
    setConfidence({});

    const result = await extractApplicationDocument(
      uploadedFile,
      uploadedFileName || uploadedFile?.name,
    );

    setScanning(false);
    setScanStatus(result.status);

    if (result.status === EXTRACTION_STATUS.COMPLETE && Object.keys(result.fields || {}).length) {
      setDraft((prev) => ({
        ...prev,
        ...result.fields,
        approvalAmount: result.fields.approvalAmount ?? prev.approvalAmount,
        personalIncome: result.fields.personalIncome ?? prev.personalIncome,
        businessIncome: result.fields.businessIncome ?? prev.businessIncome,
      }));
      if (result.bureauPullScores) {
        setBureauPullScores(result.bureauPullScores);
      }
      if (result.selectedBureauPull) {
        setSelectedBureauPull(result.selectedBureauPull);
      }
      setConfidence(result.extractionConfidence || {});
      setScanComplete(true);
      return;
    }

    setScanComplete(false);
    setConfidence({});
  };

  const handleSave = async () => {
    const resolvedEntity = drawerEntityId
      ? entities.find((entity) => entity.id === drawerEntityId) || null
      : null;
    const applicants = applicantsFromSelections(resolvedEntity, applicantSelections);
    const applicantOwnerIds = applicantOwnerIdsFromSelections(applicantSelections);
    const source = mode === 'upload' && scanComplete && scanStatus === EXTRACTION_STATUS.COMPLETE
      ? 'uploaded_scan'
      : 'manual';

    await onSave?.({
      ...draft,
      entityId: drawerEntityId || null,
      entityLabel: resolvedEntity?.businessLegalName || resolvedEntity?.dbaName || null,
      applicantOwnerIds,
      applicants,
      selectedBureauPull,
      bureauPullScores,
      experianScoreUsed: bureauPullScores.experian ?? null,
      transunionScoreUsed: bureauPullScores.transunion ?? null,
      equifaxScoreUsed: bureauPullScores.equifax ?? null,
      source,
      uploadedFileName: mode === 'upload' ? uploadedFileName : '',
      scanStatus: mode === 'upload' ? scanStatus : '',
      extractionConfidence: mode === 'upload' ? confidence : {},
      einMasked: draft.fullEin,
    });
  };

  const persBizScope = draft.personalOrBusiness || 'Unknown';
  const isPersonalScope = persBizScope === 'Personal';
  const isBusinessScope = persBizScope === 'Business';
  const businessDetailsIncomplete = isBusinessScope && (
    !drawerEntityId
    || !String(draft.fullEin || '').trim()
    || !String(draft.businessLegalName || '').trim()
    || !String(draft.businessStartDate || '').trim()
    || !String(draft.businessAddress || '').trim()
  );
  const showMissingEntityWarning = isRealClient && !hasEntities;
  const showMissingApplicantsWarning = isRealClient && !hasOwners;
  const showPersonalOnlyHint = showMissingEntityWarning && (isPersonalScope || persBizScope === 'Unknown');

  const hasOwnershipInput = Object.values(applicantSelections).some(
    (value) => value.selected
      && value.ownershipPercentUsed !== ''
      && value.ownershipPercentUsed != null
      && !Number.isNaN(Number(value.ownershipPercentUsed)),
  );
  const showOwnershipWarning = hasOwnershipInput && ownershipTotal !== 100;

  return (
    <Sheet open={open} onOpenChange={onOpenChange} modal={false}>
      <SheetContent
        side="right"
        hideOverlay
        className={`${FUNDING_RIGHT_DRAWER_CLASS} fg-create-application-sheet w-full overflow-hidden p-0 sm:max-w-2xl`}
        data-testid="create-application-drawer"
      >
        <div className="fg-create-app-drawer">
          <div className="fg-create-app-drawer-head">
            <div className="fg-create-app-title-row">
              <div>
                <h2>Create Application</h2>
                <p>Add a record manually, or upload a screenshot/document to scan fields and edit before saving.</p>
              </div>
              <button
                type="button"
                className="fg-create-app-close"
                aria-label="Close drawer"
                onClick={() => onOpenChange(false)}
              >
                <X className="size-5" />
              </button>
            </div>

            <div className="fg-create-mode-tabs" role="tablist">
              <button
                type="button"
                role="tab"
                data-testid="create-app-mode-manual"
                aria-selected={mode === 'manual'}
                className={`fg-create-mode-tab ${mode === 'manual' ? 'active' : ''}`}
                onClick={() => setMode('manual')}
              >
                Manual Entry
              </button>
              <button
                type="button"
                role="tab"
                data-testid="create-app-mode-upload"
                aria-selected={mode === 'upload'}
                className={`fg-create-mode-tab ${mode === 'upload' ? 'active' : ''}`}
                onClick={() => setMode('upload')}
              >
                Upload Screenshot / Document
              </button>
            </div>
          </div>

          <form
            className="fg-create-app-drawer-body fg-create-application-drawer fg-application-drawer"
            noValidate
            onSubmit={(event) => event.preventDefault()}
          >
            <section className="fg-create-section">
              <h3 className="fg-create-section-title">Application Entity</h3>
              <p className="fg-create-section-helper">Choose which identity, business, DBA, or entity this application belongs to.</p>
              {hasEntities ? (
                <label className="fg-create-field full">
                  <FieldLabel htmlFor="applicationEntitySelect">Entity</FieldLabel>
                  <select
                    id="applicationEntitySelect"
                    data-testid="application-entity-select"
                    className="fg-application-select"
                    value={drawerEntityId}
                    onChange={(e) => handleEntityChange(e.target.value)}
                  >
                    {entities.map((entity) => (
                      <option key={entity.id} value={entity.id}>
                        {entitySelectLabel(entity)}
                      </option>
                    ))}
                  </select>
                </label>
              ) : isRealClient ? (
                <div className="fg-create-empty-state" data-testid="application-entity-empty">
                  <p>No saved entity yet. You can still save a personal application.</p>
                  <button
                    type="button"
                    className="fg-btn fg-btn-white"
                    data-testid="open-identity-from-entity-empty"
                    onClick={() => onOpenIdentity?.()}
                  >
                    Open Identity & Business
                  </button>
                </div>
              ) : null}
              {showPersonalOnlyHint ? (
                <div className="fg-create-soft-warning" data-testid="application-personal-only-warning">
                  Personal-only application? You can save without an entity.
                </div>
              ) : null}
            </section>

            <section className="fg-create-section">
              <h3 className="fg-create-section-title">Applicants / Owners on This Application</h3>
              <p className="fg-create-section-helper">
                Select which owner, co-owner, spouse, or guarantor is being used for this application.
              </p>
              {hasOwners ? (
              <div className="fg-owner-card-list" data-testid="application-owner-list">
                {drawerEntity?.owners?.map((owner) => {
                  const sel = applicantSelections[owner.id] || {};
                  const isSelected = Boolean(sel.selected);
                  return (
                    <div
                      key={owner.id}
                      data-testid={`owner-card-${owner.id}`}
                      className={`fg-owner-card ${isSelected ? 'selected' : ''}`}
                    >
                      <div className="fg-owner-card-top">
                        <label className="fg-owner-check">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={(e) => toggleOwner(owner.id, e.target.checked)}
                            data-testid={`owner-checkbox-${owner.id}`}
                          />
                          <span className="fg-owner-name">{owner.name}</span>
                        </label>
                        <span className="fg-owner-badge">{ownerBadgeLabel(owner.role)}</span>
                      </div>
                      <div className="fg-owner-meta">
                        <span>{owner.role}</span>
                        <span>
                          Ownership {formatOwnerSensitiveValue(
                            owner.ownershipPercent != null && owner.ownershipPercent !== ''
                              ? `${owner.ownershipPercent}%`
                              : '',
                          )}
                        </span>
                        <span>SSN {formatOwnerSensitiveValue(owner.ssnMasked)}</span>
                        <span>DOB {formatOwnerSensitiveValue(owner.dob)}</span>
                      </div>
                      <div className="fg-owner-card-fields">
                        <label className="fg-create-field">
                          <FieldLabel>Role on Application</FieldLabel>
                          <select
                            className="fg-application-select"
                            value={sel.roleOnApplication || 'Co-applicant'}
                            disabled={!isSelected}
                            onChange={(e) => updateOwnerField(owner.id, 'roleOnApplication', e.target.value)}
                          >
                            {ROLE_ON_APPLICATION_OPTIONS.map((opt) => (
                              <option key={opt} value={opt}>{opt}</option>
                            ))}
                          </select>
                        </label>
                        <label className="fg-create-field">
                          <FieldLabel>Ownership % Used</FieldLabel>
                          <input
                            type="number"
                            className="fg-application-input"
                            min={0}
                            max={100}
                            disabled={!isSelected}
                            value={sel.ownershipPercentUsed ?? ''}
                            onChange={(e) => updateOwnerField(owner.id, 'ownershipPercentUsed', e.target.value)}
                            data-testid={`owner-ownership-${owner.id}`}
                          />
                        </label>
                      </div>
                    </div>
                  );
                })}
              </div>
              ) : isRealClient ? (
                <div className="fg-create-empty-state" data-testid="application-applicants-empty">
                  <p>No saved applicants yet. You can still save this application and add identity details later.</p>
                  <button
                    type="button"
                    className="fg-btn fg-btn-white"
                    data-testid="open-identity-from-applicants-empty"
                    onClick={() => onOpenIdentity?.()}
                  >
                    Open Identity & Business
                  </button>
                </div>
              ) : null}
              {showMissingApplicantsWarning ? (
                <div className="fg-create-soft-warning" data-testid="application-missing-applicants-warning">
                  No applicants selected yet. You can save now and add identity details later.
                </div>
              ) : null}
              {showOwnershipWarning ? (
                <div className="fg-ownership-warning" data-testid="ownership-warning">
                  Selected ownership totals <strong>{ownershipTotal}%</strong>. Confirm this is intentional.
                </div>
              ) : null}
            </section>

            {mode === 'upload' ? (
              <section className="funding-upload-panel fg-create-section" data-testid="upload-scan-panel">
                <div className="funding-upload-dropzone">
                  <div className="funding-upload-icon" aria-hidden>
                    <Upload className="size-8" strokeWidth={2.2} />
                  </div>
                  <h3>Upload application screenshot or document</h3>
                  <p>
                    Upload a lender screen, approval screenshot, application page, PDF, or document.
                    FundGenius will scan it and pre-fill the application fields. You can review and edit before saving.
                  </p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="fg-file-input"
                    accept=".png,.jpg,.jpeg,.webp,.heic,.pdf"
                    onChange={handleFileUpload}
                    data-testid="application-file-input"
                  />
                  <div className="funding-upload-actions">
                    <button
                      type="button"
                      className="funding-upload-btn funding-upload-btn--muted"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      Upload file
                    </button>
                    <button
                      type="button"
                      className="funding-upload-btn funding-upload-btn--primary"
                      disabled={!uploadedFile && !uploadedFileName}
                      onClick={scanAndFillFields}
                      data-testid="scan-fill-fields-btn"
                    >
                      {scanning ? 'Scanning…' : 'Scan & Fill Fields'}
                    </button>
                    <button
                      type="button"
                      className="funding-upload-btn funding-upload-btn--danger"
                      onClick={clearUpload}
                    >
                      Clear upload
                    </button>
                  </div>
                </div>

                <div className="funding-upload-file-card">
                  <div className="funding-upload-file-icon">
                    {uploadedFileName?.toLowerCase().endsWith('.pdf') ? 'PDF' : 'IMG'}
                  </div>
                  <div>
                    <strong data-testid="upload-file-name">{uploadedFileName || 'No file selected'}</strong>
                    <span>{uploadedFile ? `${Math.round(uploadedFile.size / 1024)} KB` : 'Upload an image or PDF to scan.'}</span>
                  </div>
                  <span className="funding-upload-status">
                    {scanStatus === EXTRACTION_STATUS.COMPLETE
                      ? 'Scanned'
                      : scanStatus === EXTRACTION_STATUS.UNAVAILABLE
                        ? 'Extraction unavailable'
                        : scanning
                          ? 'Scanning'
                          : 'Ready'}
                  </span>
                </div>

                <div className="fg-scan-status" data-testid="scan-status-box">
                  <strong>
                    {scanning
                      ? 'Scanning'
                      : scanStatus === EXTRACTION_STATUS.COMPLETE
                        ? 'Complete'
                        : scanStatus === EXTRACTION_STATUS.UNAVAILABLE
                          ? 'Extraction unavailable'
                          : scanStatus === EXTRACTION_STATUS.ERROR
                            ? 'Error'
                            : scanStatus === 'ready'
                              ? 'Ready'
                              : 'Idle'}
                  </strong>
                  <div>
                    {scanning
                      ? 'Checking uploaded document for extractable fields…'
                      : scanStatus === EXTRACTION_STATUS.COMPLETE
                        ? 'Fields extracted from the uploaded document.'
                        : scanStatus === EXTRACTION_STATUS.UNAVAILABLE
                          ? EXTRACTION_UNAVAILABLE_DETAIL
                          : scanStatus === EXTRACTION_STATUS.ERROR
                            ? 'Extraction failed. Enter fields manually.'
                            : scanStatus === 'ready'
                              ? 'File ready — click Scan & Fill Fields.'
                              : 'Upload a file to begin.'}
                  </div>
                </div>
                {scanComplete ? (
                  <div className="fg-review-banner" data-testid="review-banner">
                    Fields extracted from your upload. Review every field below and edit anything that is incorrect before saving.
                  </div>
                ) : null}
                {scanStatus === EXTRACTION_STATUS.UNAVAILABLE && !scanning ? (
                  <div className="fg-extraction-unavailable" data-testid="application-extraction-unavailable">
                    <h4>{EXTRACTION_UNAVAILABLE_TITLE}</h4>
                    <p>{EXTRACTION_UNAVAILABLE_DETAIL}</p>
                  </div>
                ) : null}
              </section>
            ) : null}

            <section className="fg-create-section">
              <h3 className="fg-create-section-title">Product & Lender</h3>
              <div className="fg-create-field-grid">
                <label className="fg-create-field">
                  <FieldLabel confidence={confidence.dateOfApplication}>Date of Application</FieldLabel>
                  <input
                    type="date"
                    className="fg-application-input"
                    value={draft.dateOfApplication ? String(draft.dateOfApplication).slice(0, 10) : ''}
                    onChange={(e) => setField('dateOfApplication', e.target.value)}
                  />
                </label>
                <FundingCustomSelect
                  themed
                  label={<>Lender <ConfidenceBadge level={confidence.lender} /></>}
                  value={draft.lender}
                  options={LENDER_OPTIONS}
                  onChange={(v) => setField('lender', v)}
                />
                <FundingCustomSelect
                  themed
                  label={<>Lending Product <ConfidenceBadge level={confidence.lendingProduct} /></>}
                  value={draft.lendingProduct}
                  options={LENDING_PRODUCT_OPTIONS}
                  onChange={(v) => setField('lendingProduct', v)}
                />
                <FundingCustomSelect
                  themed
                  label={<>Type <ConfidenceBadge level={confidence.applicationType} /></>}
                  value={draft.applicationType}
                  options={APPLICATION_TYPE_OPTIONS}
                  onChange={(v) => setField('applicationType', v)}
                />
                <FundingCustomSelect
                  themed
                  label={<>Pers/Biz <ConfidenceBadge level={confidence.personalOrBusiness} /></>}
                  value={draft.personalOrBusiness || 'Unknown'}
                  options={PERS_BIZ_OPTIONS}
                  onChange={(v) => setField('personalOrBusiness', v)}
                />
              </div>
              {businessDetailsIncomplete ? (
                <div className="fg-create-soft-warning" data-testid="application-business-incomplete-warning">
                  Business details are incomplete. You can save now and complete later.
                </div>
              ) : null}
            </section>

            <section className="fg-create-section">
              <h3 className="fg-create-section-title">Decision</h3>
              <div className="fg-create-field-grid">
                <FundingCustomSelect
                  themed
                  label={<>Decision <ConfidenceBadge level={confidence.decision} /></>}
                  value={draft.decision}
                  options={DECISION_OPTIONS}
                  onChange={(v) => setField('decision', v)}
                />
                <label className="fg-create-field">
                  <FieldLabel confidence={confidence.approvalAmount}>Approval Amount</FieldLabel>
                  <input
                    type="number"
                    className="fg-application-input"
                    value={draft.approvalAmount}
                    onChange={(e) => setField('approvalAmount', e.target.value)}
                  />
                </label>
                <label className="fg-create-field">
                  <FieldLabel confidence={confidence.zeroPercentOffer}>0% Offer</FieldLabel>
                  <select
                    className="fg-application-select"
                    value={draft.zeroPercentOffer}
                    onChange={(e) => setField('zeroPercentOffer', e.target.value)}
                  >
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                </label>
                <FundingCustomSelect
                  themed
                  label={<>0% Term <ConfidenceBadge level={confidence.zeroPercentTerm} /></>}
                  value={draft.zeroPercentTerm}
                  options={ZERO_PERCENT_TERM_OPTIONS}
                  onChange={(v) => setField('zeroPercentTerm', v)}
                />
              </div>
            </section>

            <section className="fg-create-section">
              <h3 className="fg-create-section-title">Bureau Pull</h3>
              <p className="fg-create-section-helper">Select which bureau pull was used for this application.</p>
              <BureauPullSelector
                bureauPullScores={bureauPullScores}
                selectedBureauPull={selectedBureauPull}
                onSelect={(key) => {
                  setSelectedBureauPull(key);
                  setDraft((prev) => ({ ...prev, selectedBureauPull: key }));
                }}
              />
            </section>

            <section className="fg-create-section">
              <h3 className="fg-create-section-title">Identity & Application Details</h3>
              <div className="fg-create-field-grid">
                <label className="fg-create-field">
                  <FieldLabel confidence={confidence.ownerName}>Owner Name</FieldLabel>
                  <input className="fg-application-input" value={draft.ownerName || ''} onChange={(e) => setField('ownerName', e.target.value)} />
                </label>
                <label className="fg-create-field">
                  <FieldLabel>SSN</FieldLabel>
                  <input className="fg-application-input" value={draft.ssnMasked || ''} readOnly placeholder="***-**-XXXX" />
                </label>
                <label className="fg-create-field">
                  <FieldLabel>DOB</FieldLabel>
                  <input
                    type="date"
                    className="fg-application-input"
                    value={draft.dob ? String(draft.dob).slice(0, 10) : ''}
                    onChange={(e) => setField('dob', e.target.value)}
                  />
                </label>
                <label className="fg-create-field">
                  <FieldLabel>Full EIN</FieldLabel>
                  <div className="fg-ein-masked">
                    <SecureEinCell value={draft.fullEin} />
                  </div>
                </label>
                <label className="fg-create-field">
                  <FieldLabel confidence={confidence.businessLegalName}>Business Legal Name</FieldLabel>
                  <input className="fg-application-input" value={draft.businessLegalName || ''} onChange={(e) => setField('businessLegalName', e.target.value)} />
                </label>
                <label className="fg-create-field">
                  <FieldLabel>Business Start Date</FieldLabel>
                  <input
                    type="date"
                    className="fg-application-input"
                    value={draft.businessStartDate ? String(draft.businessStartDate).slice(0, 10) : ''}
                    onChange={(e) => setField('businessStartDate', e.target.value)}
                  />
                </label>
                <label className="fg-create-field">
                  <FieldLabel>Employer</FieldLabel>
                  <input className="fg-application-input" value={draft.employerName || ''} onChange={(e) => setField('employerName', e.target.value)} />
                </label>
                <TimeDurationSelect
                  themed
                  label="Time Live in House"
                  years={draft.timeLiveInHouseYears}
                  months={draft.timeLiveInHouseMonths}
                  onYearsChange={(v) => setField('timeLiveInHouseYears', v)}
                  onMonthsChange={(v) => setField('timeLiveInHouseMonths', v)}
                />
                <TimeDurationSelect
                  themed
                  label="Time Working At"
                  years={draft.timeWorkingAtYears}
                  months={draft.timeWorkingAtMonths}
                  onYearsChange={(v) => setField('timeWorkingAtYears', v)}
                  onMonthsChange={(v) => setField('timeWorkingAtMonths', v)}
                />
                <label className="fg-create-field full">
                  <FieldLabel>Personal Address</FieldLabel>
                  <input className="fg-application-input" value={draft.personalAddress || ''} onChange={(e) => setField('personalAddress', e.target.value)} />
                </label>
                <label className="fg-create-field full">
                  <FieldLabel>Business Address</FieldLabel>
                  <input className="fg-application-input" value={draft.businessAddress || ''} onChange={(e) => setField('businessAddress', e.target.value)} />
                </label>
              </div>
            </section>

            <section className="fg-create-section">
              <h3 className="fg-create-section-title">Income & Assets</h3>
              <div className="fg-create-field-grid">
                {[
                  ['personalIncome', 'Personal Income'],
                  ['businessIncome', 'Business Income'],
                  ['housing', 'Housing'],
                  ['businessExpenses', 'Business Expenses'],
                  ['savings', 'Savings'],
                  ['retirementAccount', 'Retirement / Stocks'],
                ].map(([key, label]) => (
                  <label key={key} className="fg-create-field">
                    <FieldLabel>{label}</FieldLabel>
                    <input
                      type="number"
                      className="fg-application-input"
                      value={draft[key]}
                      onChange={(e) => setField(key, e.target.value)}
                    />
                  </label>
                ))}
              </div>
            </section>

            <section className="fg-create-section">
              <h3 className="fg-create-section-title">Notes</h3>
              <textarea
                className="fg-application-textarea fg-notes-area"
                rows={4}
                value={draft.notes || ''}
                onChange={(e) => setField('notes', e.target.value)}
              />
            </section>
          </form>

          <div className="fg-create-app-drawer-footer">
            <Button type="button" variant="outline" className="flex-1 rounded-xl" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              className="flex-1 rounded-xl bg-[var(--fg-accent)]"
              onClick={handleSave}
              disabled={saving}
              data-testid="save-application-btn"
            >
              {saving ? 'Saving…' : 'Save Application'}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default CreateApplicationDrawer;
